<template>
  <div id="app">
    <v-app>
      <v-app-bar color="primary" flat class="app-bar">
        <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
        <v-img @click="$router.push('/')" style="cursor:pointer; flex: 0 1 auto;" alt="Bahnomat Logo" class="mr-3" src="/images/logo.png" transition="scale-transition" width="38" height="38" />
        <div class="app-brand" @click="$router.push('/')">
          <h1 class="app-title font-display">Bahnomat</h1>
          <span class="app-tagline d-none d-md-flex">Pünktlichkeit auf Knopfdruck</span>
        </div>
        <v-spacer />
        <span class="app-bar-meta font-mono d-none d-sm-flex">
          <span class="live-dot" /> live
        </span>
      </v-app-bar>

      <v-navigation-drawer v-model="drawer">
        <SearchWidget />
        <v-divider />
        <v-list density="compact" nav>
          <template v-for="route in routes" :key="route.name">
            <v-list-item
              v-if="route.meta"
              :to="route.meta.path"
              :title="route.meta.title"
              :prepend-icon="route.meta.icon"
              :append-icon="lockIcon(route)"
            />
          </template>
        </v-list>

        <template v-slot:append>
          <v-list density="compact" nav>
            <v-divider class="mb-1" />
            <v-list-item
              v-if="oidcIsAuthenticated"
              :title="logoutLabel"
              prepend-icon="mdi-logout"
              @click="signOutOidc()"
            />
            <v-list-item
              v-else
              title="Anmelden"
              subtitle="Volle Historie freischalten"
              prepend-icon="mdi-login"
              @click="signIn()"
            />
          </v-list>
        </template>
      </v-navigation-drawer>

      <v-main class="d-flex flex-column">
        <v-container fluid class="flex-grow-1">
          <router-view />
        </v-container>
        <v-footer class="app-footer" color="transparent">
          <div class="footer-in">
            <span class="footer-brand">
              © {{ year }} Bahnomat · kein offizielles Angebot der DB
            </span>
            <span class="footer-links">
              <router-link to="/impressum">Impressum</router-link>
            </span>
          </div>
        </v-footer>
      </v-main>
    </v-app>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SearchWidget from '@/components/SearchWidget.vue'
import SignInMixin from '@/mixins/SignInMixin'
import NotificationMixin from '@/mixins/NotificationMixin'

export default {
  name: 'App',

  components: {
    SearchWidget,
  },

  mixins: [SignInMixin, NotificationMixin],

  data: () => ({
    drawer: null,
  }),

  created: function() {
    this.syncNotificationSubscriptions()
  },

  computed: {
    routes: function() {
      return this.$router.options.routes.filter(route =>
        (!route.meta || !route.meta.hidden) &&
        (!route.meta || !route.meta.requiresAdmin || this.$store.state.admin) &&
        (!route.meta || !route.meta.requiresNotifications || this.$store.state.notifications.subscriptions.length > 0))
    },

    year: function() {
      return new Date().getFullYear()
    },

    logoutLabel: function() {
      const name = this.oidcUser && (this.oidcUser.name || this.oidcUser.preferred_username)
      return name ? `Abmelden (${name})` : 'Abmelden'
    },

    ...mapGetters('oidcStore', [
      'oidcUser',
      'oidcIsAuthenticated'
    ]),
  },

  methods: {
    ...mapActions('oidcStore', [
      'signOutOidc'
    ]),

    // Same idiom as the locked options in SearchWidget's "Zeitraum" select: show the entry and
    // mark it, rather than hiding it and leaving no hint that signing in unlocks more. The page
    // itself carries the explanation, so the icon is a signpost and not the only signal.
    lockIcon: function(route) {
      return route.meta.requiresAuth && !this.oidcIsAuthenticated ? 'mdi-lock' : undefined
    },

    refreshSession: function() {
      this.$api
        .get('/me')
        .then(response => {
          this.$store.commit('setAdmin', !!response.data.admin)
          // Absent means unrestricted — an older backend or a deployment with no cap.
          this.$store.commit('setAnonymousHistoryDays', response.data.anonymousHistoryDays ?? null)
        })
        .catch(() => {
          // Fail closed on privilege: an unreachable /api/me must not leave admin-only UI open.
          this.$store.commit('setAdmin', false)
          // But leave anonymousHistoryDays untouched: a transient failure is not evidence that
          // the caller is unrestricted, and wrongly unlocking it here would let an anonymous
          // visitor pick a range the backend then rejects with a 400.
        })
    },
  },

  watch: {
    oidcUser: {
      immediate: true,
      handler: function() {
        this.refreshSession()
      },
    },
  },
};
</script>

<style lang="scss">
  // Typography: distinctive display + body + mono, replacing the old Roboto.
  .v-application {
    font-family: 'Hanken Grotesk', system-ui, -apple-system, sans-serif;
  }
  .v-application h1,
  .v-application h2,
  .v-application h3 {
    font-family: 'Bricolage Grotesque', sans-serif;
    letter-spacing: -0.015em;
  }
  .font-display { font-family: 'Bricolage Grotesque', sans-serif; }
  .font-mono { font-family: 'Spline Sans Mono', monospace; }

  // App bar
  .app-brand {
    display: flex;
    flex-direction: column;
    justify-content: center;
    cursor: pointer;
    line-height: 1.05;
  }
  .app-title {
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
    margin: 0;
  }
  .app-tagline {
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    opacity: 0.82;
    margin-top: 2px;
  }
  .app-bar-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: 18px;
    font-size: 0.78rem;
    letter-spacing: 0.06em;
    opacity: 0.85;
  }
  .live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #46e3a0;
    box-shadow: 0 0 9px #46e3a0;
    animation: live-blink 1.5s steps(1) infinite;
  }
  @keyframes live-blink { 50% { opacity: 0.3; } }

  // Footer
  .app-footer {
    border-top: 1px solid #e6e9f2;
    padding: 16px 24px;
  }
  .footer-in {
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
    align-items: center;
    justify-content: space-between;
    font-size: 0.85rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
  .footer-links {
    display: flex;
    gap: 20px;
  }
  .footer-links a {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
    text-decoration: none;
  }
  .footer-links a:hover {
    text-decoration: underline;
  }

  .cancelled {
    text-decoration: line-through;
  }

  .nextStop {
    font-weight: bold;
  }

  .enum:not(:last-of-type):after {
    content: ", ";
  }

  .disruption {
    color: red;
  }

  // Make trip rows read as separate cards instead of one joined block.
  .v-expansion-panels {
    gap: 12px;
  }
  .v-expansion-panel {
    border-radius: 14px !important;
    border: 1px solid #e6e9f2;
    box-shadow: 0 1px 2px rgba(16, 24, 64, 0.05), 0 8px 24px -14px rgba(16, 24, 64, 0.18);
  }
  .v-expansion-panel::after {
    display: none; // remove the default divider between joined panels
  }
  .v-expansion-panel__shadow {
    display: none; // we provide our own shadow above
  }
</style>
